// Learning Tracks query layer. Pairs with `lib/mutations/learning-tracks.ts`.
//
// Tracks are curated, ordered lists of existing resources. The list endpoint
// already gates by viewer role on the server (admins see drafts; non-admins
// only see published). The detail endpoint returns ordered items, each
// carrying an `availability` verdict for this viewer and, exactly when that
// verdict is `available`, the canonical `ResourceSummary` (the same DTO the
// resources library renders). `fetchLearningTrack` parses each item into a
// discriminated union so the pairing is enforced by the type.

import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import type { components } from '@leanin/api-client';
import { getApiClient } from '../api-client';
import { unwrap } from '../api-error';
import { QUERY_KEY_REGISTRY } from './normalize';

// ---- Public types ----

export type LearningTrackSummary =
  components['schemas']['LearningTrackSummary'];
type LearningTrackItemWire = components['schemas']['LearningTrackItem'];
type LearningTrackDetailWire = components['schemas']['LearningTrackDetail'];
type TrackResourceSummary = components['schemas']['ResourceSummary'];

export type LearningTrackItemAvailability = LearningTrackItemWire['availability'];

// `sign_in_required`: the viewer is signed out and any signed-in member could
// open it. `unavailable`: unpublished, held by moderation, or gated to a
// Network the viewer is not in.
export type LearningTrackItem = Omit<
  LearningTrackItemWire,
  'availability' | 'resource'
> &
  (
    | { readonly availability: 'available'; readonly resource: TrackResourceSummary }
    | {
        readonly availability: Exclude<LearningTrackItemAvailability, 'available'>;
        readonly resource: null;
      }
  );

export type LearningTrackDetail = Omit<LearningTrackDetailWire, 'items'> & {
  readonly items: readonly LearningTrackItem[];
};
export type LearningTrackCursorPage =
  components['schemas']['LearningTrackCursorPage'];

export type LearningTracksListParams = {
  readonly q?: string;
  /** Topic slug — only tracks containing a resource tagged with it. */
  readonly tag?: string;
  readonly pageSize?: number;
};

// ---- Query keys + leaf factories ----
//
// `all`/`lists` are invalidation prefixes (bare arrays). `list`/`detail` are
// leaves returning `infiniteQueryOptions`/`queryOptions` — key built adjacent
// to the `queryFn` from one normalized `params` object (decision 3).

export const learningTracksKeys = {
  all: ['learning-tracks'] as const,
  lists: () => [...learningTracksKeys.all, 'list'] as const,

  list: (params: LearningTracksListParams) =>
    infiniteQueryOptions({
      queryKey: [...learningTracksKeys.lists(), params] as const,
      queryFn: ({ pageParam }) =>
        fetchLearningTracks({
          ...params,
          ...(pageParam !== undefined ? { cursor: pageParam } : {}),
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      placeholderData: keepPreviousData,
    }),

  detail: (id: string) =>
    queryOptions({
      queryKey: [...learningTracksKeys.all, 'detail', id] as const,
      queryFn: () => fetchLearningTrack(id),
    }),
};

// ---- Fetchers ----

export async function fetchLearningTracks(
  params: LearningTracksListParams & { cursor?: string },
): Promise<LearningTrackCursorPage> {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/learning-tracks', {
    params: {
      query: {
        ...(params.q !== undefined && params.q.length > 0
          ? { q: params.q }
          : {}),
        ...(params.tag !== undefined && params.tag.length > 0
          ? { tag: params.tag }
          : {}),
        ...(params.cursor !== undefined ? { cursor: params.cursor } : {}),
        ...(params.pageSize !== undefined
          ? { pageSize: params.pageSize }
          : {}),
      },
    },
  }));
  return data;
}

export async function fetchLearningTrack(
  id: string,
): Promise<LearningTrackDetail> {
  const client = getApiClient();
  const data = unwrap(await client.GET(
    '/api/v1/learning-tracks/{id}',
    { params: { path: { id } } },
  ));
  return { ...data, items: data.items.map(parseLearningTrackItem) };
}

// The server contract: `resource` is non-null exactly when `availability` is
// `available`. A response breaking that is a server bug, surfaced as a load
// failure rather than rendered as a half-populated row.
function parseLearningTrackItem(item: LearningTrackItemWire): LearningTrackItem {
  const { availability, resource, ...rest } = item;
  if (availability === 'available' && resource !== null) {
    return { ...rest, availability, resource };
  }
  if (availability !== 'available' && resource === null) {
    return { ...rest, availability, resource };
  }
  throw new Error(
    `LEARNING_TRACK_ITEM_CONTRACT: item ${item.resourceId} has availability "${availability}" with resource ${resource === null ? 'null' : 'present'}`,
  );
}

// ---- Hooks ----

export function useInfiniteLearningTracks(
  params: LearningTracksListParams,
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery({ ...learningTracksKeys.list(params), ...options });
}

export function useLearningTrack(
  id: string,
  options?: { enabled?: boolean; retry?: boolean | number; retryOnMount?: boolean },
) {
  return useQuery({ ...learningTracksKeys.detail(id), ...options });
}

// ---- Resource picker (uses the resources/search endpoint the rest of the
//      product picks resources through). Shaped to match the picker shape
//      `TypeaheadMultiSelect` consumes. ----

export type LearningTrackResourcePickerItem = {
  readonly id: string;
  readonly title: string;
  readonly kind: string;
  readonly publishedAt: string | null;
};

// Curation pickers (learning-track admin, topic curation) attach DRAFTS
// too, so this searches the admin listing — the member-facing /resources
// endpoints compose `readable`, which hides unpublished work. Both
// consumers are admin-only surfaces; a non-admin caller would get 403.
export async function searchResourcesForPicker(
  q: string,
): Promise<readonly LearningTrackResourcePickerItem[]> {
  const trimmed = q.trim();
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/admin/resources', {
    params: {
      query: {
        pageSize: 20,
        // Below 2 chars the server applies no text filter — an unfiltered
        // recency page, which is what an just-opened picker should show.
        ...(trimmed.length > 0 ? { q: trimmed } : {}),
      },
    },
  }));
  return data.items.map((r) => ({
    id: r.id,
    title: r.title,
    kind: r.kind,
    publishedAt: r.publishedAt,
  }));
}

// ---- Query-key registry (decision 6 invariant test) ----

QUERY_KEY_REGISTRY.push(
  {
    name: 'learningTracksKeys.list',
    sampleKey: learningTracksKeys.list({ q: 'leadership', pageSize: 20 })
      .queryKey,
  },
  {
    name: 'learningTracksKeys.detail',
    sampleKey: learningTracksKeys.detail('track-1').queryKey,
  },
);
